// Header.js
import React, { useState, useEffect } from 'react';
import { formatHelpers } from '../../utils/apiService';

const Header = ({ user, onToggleSidebar, onLogout }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleDropdownToggle = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleClickOutside = (e) => {
    if (!e.target.closest('.user-dropdown')) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Administrator';
      case 'admin':
        return 'Administrator';
      case 'user':
        return 'User';
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'badge-error';
      case 'admin':
        return 'badge-warning';
      case 'user':
        return 'badge-info';
      default:
        return 'badge-info';
    }
  };

  return (
    <header className="dashboard-header-bar">
      <div className="flex items-center justify-between w-full">
        {/* Left side - Menu toggle and title */}
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleSidebar}
            className="header-menu-toggle"
            aria-label="Toggle sidebar"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          <div className="header-title-section">
            <h1 className="header-title">OOU EPROFICIENCY System</h1>
            <p className="header-subtitle">
              {formatHelpers.formatDateTime(currentTime)}
            </p>
          </div>
        </div>

        {/* Right side - User info and actions */}
        <div className="flex items-center gap-4">
          {/* Notifications (placeholder) */}
          <div className="header-notifications">
            <button className="notification-btn" title="Notifications">
              🔔
              <span className="notification-badge">3</span>
            </button>
          </div>

          {/* User dropdown */}
          <div className="user-dropdown relative">
            <button
              onClick={handleDropdownToggle}
              className="user-dropdown-trigger"
              aria-expanded={dropdownOpen}
            >
              <div className="user-avatar">
                {user.firstname.charAt(0)}{user.lastname.charAt(0)}
              </div>
              <div className="user-info">
                <div className="user-name">
                  {user.firstname} {user.lastname}
                </div>
                <div className={`badge ${getRoleBadgeColor(user.role)}`}>
                  {getRoleDisplayName(user.role)}
                </div>
              </div>
              <span className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>
                ▼
              </span>
            </button>

            {/* Dropdown menu */}
            {dropdownOpen && (
              <div className="user-dropdown-menu">
                <div className="dropdown-header">
                  <div className="user-avatar-large">
                    {user.firstname.charAt(0)}{user.lastname.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold">{user.firstname} {user.lastname}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className={`badge ${getRoleBadgeColor(user.role)} mt-1`}>
                      {getRoleDisplayName(user.role)}
                    </div>
                  </div>
                </div>

                <div className="dropdown-divider"></div>

                <button
                  onClick={onLogout}
                  className="dropdown-item dropdown-item-danger"
                >
                  <span className="dropdown-icon">🚪</span>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Header Styles */}
      <style jsx>{`
        .dashboard-header-bar {
          background: white;
          border-bottom: 1px solid var(--gray-200);
          padding: 1rem 2rem;
          position: sticky;
          top: 0;
          z-index: 30;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .header-menu-toggle {
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          width: 24px;
          height: 24px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }

        .hamburger-line {
          width: 100%;
          height: 2px;
          background: var(--gray-600);
          transition: all 0.3s ease;
        }

        .header-menu-toggle:hover .hamburger-line {
          background: var(--primary-blue);
        }

        .header-title-section {
          display: none;
        }

        .header-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--gray-800);
          margin: 0;
        }

        .header-subtitle {
          font-size: 0.875rem;
          color: var(--gray-500);
          margin: 0;
        }

        .header-notifications {
          position: relative;
        }

        .notification-btn {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 50%;
          transition: background-color 0.3s ease;
          position: relative;
        }

        .notification-btn:hover {
          background: var(--gray-100);
        }

        .notification-badge {
          position: absolute;
          top: 0;
          right: 0;
          background: var(--error-red);
          color: white;
          font-size: 0.625rem;
          font-weight: bold;
          padding: 0.125rem 0.375rem;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
        }

        .user-dropdown-trigger {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 0.5rem;
          transition: all 0.3s ease;
        }

        .user-dropdown-trigger:hover {
          background: var(--gray-50);
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .user-avatar-large {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1rem;
        }

        .user-info {
          text-align: left;
          display: none;
        }

        .user-name {
          font-weight: 500;
          color: var(--gray-800);
          font-size: 0.875rem;
        }

        .dropdown-arrow {
          color: var(--gray-500);
          font-size: 0.75rem;
          transition: transform 0.3s ease;
          display: none;
        }

        .dropdown-arrow.open {
          transform: rotate(180deg);
        }

        .user-dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid var(--gray-200);
          border-radius: 0.5rem;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          min-width: 240px;
          z-index: 50;
          margin-top: 0.5rem;
        }

        .dropdown-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          border-bottom: 1px solid var(--gray-100);
        }

        .dropdown-divider {
          height: 1px;
          background: var(--gray-100);
          margin: 0.5rem 0;
        }

        .dropdown-items {
          padding: 0.5rem 0;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
          padding: 0.75rem 1rem;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          transition: background-color 0.3s ease;
          font-size: 0.875rem;
        }

        .dropdown-item:hover {
          background: var(--gray-50);
        }

        .dropdown-item-danger:hover {
          background: var(--red-50);
          color: var(--error-red);
        }

        .dropdown-icon {
          font-size: 1rem;
        }

        @media (min-width: 768px) {
          .header-title-section {
            display: block;
          }

          .user-info {
            display: block;
          }

          .dropdown-arrow {
            display: block;
          }
        }

        @media (max-width: 768px) {
          .dashboard-header-bar {
            padding: 1rem;
          }

          .user-dropdown-menu {
            right: -1rem;
            left: -1rem;
            min-width: auto;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;